import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ESTADOS_SUSCRIPCION,
  ETIQUETA_ESTADO_SUSCRIPCION,
  formatearFecha,
  formatearFechaHora,
  formatearMoneda,
  type EstadoSuscripcion,
  type PlanInfo,
  type TenantDetalle,
  type TenantResumen,
} from '@cmms/shared';
import { api, ErrorApi } from '../../servicios/api.js';
import { ErroresFormulario } from '../../componentes/piezas.js';
import { useSesionOperador } from '../../servicios/sesion-operador.js';
import { Aviso, Cargando, Etiqueta, Indicador, Vacio } from '../../componentes/piezas.js';
import { BotonVolver } from '../../componentes/boton-volver.js';

const TONO_ESTADO: Record<string, 'verde' | 'azul' | 'ambar' | 'roja' | 'gris'> = {
  Activa: 'verde',
  'En prueba': 'azul',
  Suspendida: 'ambar',
  Cancelada: 'roja',
};

export function ListaTenants(): JSX.Element {
  const { puede } = useSesionOperador();
  const cliente = useQueryClient();
  const [creando, setCreando] = useState(false);

  const { data: tenants, isLoading } = useQuery({
    queryKey: ['plataforma', 'tenants'],
    queryFn: () => api.get<TenantResumen[]>('/plataforma/tenants'),
  });

  const { data: planes } = useQuery({
    queryKey: ['plataforma', 'planes'],
    queryFn: () => api.get<PlanInfo[]>('/plataforma/planes'),
  });

  const crear = useMutation({
    mutationFn: (datos: unknown) => api.post<TenantResumen>('/plataforma/tenants', datos),
    onSuccess: async () => {
      setCreando(false);
      await cliente.invalidateQueries({ queryKey: ['plataforma'] });
    },
  });

  if (isLoading) return <Cargando />;

  return (
    <>
      <div className="cabecera-pagina">
        <div>
          <h1>Organizaciones</h1>
          <p>Alta, plan y estado de cada cliente. El detalle abre el histórico de licencia.</p>
        </div>
        {puede('tenant:escribir') ? (
          <button type="button" className="boton" onClick={() => setCreando((v) => !v)}>
            {creando ? 'Cancelar' : 'Nueva organización'}
          </button>
        ) : null}
      </div>

      {crear.isError ? <ErroresFormulario error={crear.error} /> : null}

      {creando ? (
        <FormularioAlta
          planes={planes ?? []}
          enviando={crear.isPending}
          onEnviar={(datos) => crear.mutate(datos)}
        />
      ) : null}

      {tenants && tenants.length > 0 ? (
        <section className="panel">
          <div className="tabla-contenedor">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Organización</th>
                  <th>Plan</th>
                  <th>Estado</th>
                  <th>Asientos</th>
                  <th>Total mensual</th>
                  <th>Alta</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((t) => (
                  <tr key={t.id}>
                    <td>
                      <Link to={`/plataforma/organizaciones/${t.id}`} style={{ fontWeight: 600 }}>
                        {t.razonSocial}
                      </Link>
                      <div style={{ fontSize: 11.5, color: 'var(--texto-tenue)' }}>
                        {t.slug}
                        {t.ruc ? ` · RUC ${t.ruc}` : ''}
                        {t.activo ? '' : ' · inactiva'}
                      </div>
                    </td>
                    <td>{t.plan ?? '—'}</td>
                    <td>
                      {t.estadoSuscripcion ? (
                        <Etiqueta tono={TONO_ESTADO[t.estadoSuscripcion] ?? 'gris'}>
                          {t.estadoSuscripcion}
                        </Etiqueta>
                      ) : (
                        <Etiqueta tono="roja">Sin suscripción</Etiqueta>
                      )}
                    </td>
                    <td className="numero-registro">
                      {t.asientosConsumidos} / {t.asientosContratados ?? '—'}
                    </td>
                    <td>{t.totalMensualPen ? formatearMoneda(t.totalMensualPen) : '—'}</td>
                    <td>{formatearFecha(t.creadoEl)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : (
        <Vacio titulo="Sin organizaciones" detalle="Cree la primera desde «Nueva organización»." />
      )}
    </>
  );
}

/**
 * Alta de organización.
 *
 * El primer administrador va en el mismo formulario porque un tenant sin cuenta
 * capaz de entrar no le sirve a nadie, y separarlo en dos pasos deja abierta la
 * ventana en que la organización existe, factura y nadie puede acceder.
 */
function FormularioAlta({
  planes,
  enviando,
  onEnviar,
}: {
  planes: PlanInfo[];
  enviando: boolean;
  onEnviar: (datos: unknown) => void;
}): JSX.Element {
  const [slug, setSlug] = useState('');
  const [razonSocial, setRazonSocial] = useState('');
  const [ruc, setRuc] = useState('');
  const [planCodigo, setPlanCodigo] = useState(planes[0]?.codigo ?? '');
  const [asientos, setAsientos] = useState(5);
  const [estado, setEstado] = useState<'PRUEBA' | 'ACTIVA'>('PRUEBA');
  const [nombreAdmin, setNombreAdmin] = useState('');
  const [emailAdmin, setEmailAdmin] = useState('');
  const [contrasenaAdmin, setContrasenaAdmin] = useState('');

  return (
    <section className="panel" style={{ marginBottom: 16 }}>
      <div className="panel-cabecera">
        <h2>Nueva organización</h2>
      </div>
      <form
        className="panel-cuerpo"
        onSubmit={(e) => {
          e.preventDefault();
          onEnviar({
            slug,
            razonSocial,
            ruc: ruc || undefined,
            planCodigo,
            asientosContratados: asientos,
            estado,
            administrador: {
              nombreCompleto: nombreAdmin,
              email: emailAdmin,
              contrasena: contrasenaAdmin,
            },
          });
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          <div className="campo">
            <label htmlFor="slug">Identificador (subdominio)</label>
            <input
              id="slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase())}
              pattern="[a-z0-9][a-z0-9-]{1,49}"
              required
            />
          </div>
          <div className="campo">
            <label htmlFor="razon">Razón social</label>
            <input id="razon" value={razonSocial} onChange={(e) => setRazonSocial(e.target.value)} required />
          </div>
          <div className="campo">
            <label htmlFor="ruc">RUC (opcional)</label>
            <input id="ruc" value={ruc} onChange={(e) => setRuc(e.target.value)} inputMode="numeric" maxLength={11} />
          </div>
          <div className="campo">
            <label htmlFor="plan">Plan</label>
            <select id="plan" value={planCodigo} onChange={(e) => setPlanCodigo(e.target.value)} required>
              {planes
                .filter((p) => p.activo)
                .map((p) => (
                  <option key={p.codigo} value={p.codigo}>
                    {p.nombre} · {formatearMoneda(p.precioAsientoMensualPen)}/asiento
                  </option>
                ))}
            </select>
          </div>
          <div className="campo">
            <label htmlFor="asientos-nuevo">Asientos contratados</label>
            <input
              id="asientos-nuevo"
              type="number"
              min={1}
              max={10000}
              value={asientos}
              onChange={(e) => setAsientos(Number(e.target.value))}
              required
            />
          </div>
          <div className="campo">
            <label htmlFor="estado-nuevo">Estado inicial</label>
            <select
              id="estado-nuevo"
              value={estado}
              onChange={(e) => setEstado(e.target.value as 'PRUEBA' | 'ACTIVA')}
            >
              <option value="PRUEBA">En prueba</option>
              <option value="ACTIVA">Activa</option>
            </select>
          </div>
        </div>

        <h3 style={{ fontSize: 13, margin: '18px 0 10px', color: 'var(--texto-medio)' }}>
          PRIMER ADMINISTRADOR
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          <div className="campo">
            <label htmlFor="admin-nombre">Nombre completo</label>
            <input id="admin-nombre" value={nombreAdmin} onChange={(e) => setNombreAdmin(e.target.value)} required />
          </div>
          <div className="campo">
            <label htmlFor="admin-email">Correo</label>
            <input
              id="admin-email"
              type="email"
              value={emailAdmin}
              onChange={(e) => setEmailAdmin(e.target.value)}
              required
            />
          </div>
          <div className="campo">
            <label htmlFor="admin-contrasena">Contraseña inicial</label>
            <input
              id="admin-contrasena"
              type="text"
              value={contrasenaAdmin}
              onChange={(e) => setContrasenaAdmin(e.target.value)}
              minLength={12}
              required
            />
          </div>
        </div>
        <p style={{ fontSize: 12, color: 'var(--texto-tenue)', margin: '4px 0 14px' }}>
          Comuníquela por un canal seguro y pida que la cambie al primer ingreso.
        </p>

        <button type="submit" className="boton" disabled={enviando || planes.length === 0}>
          {enviando ? 'Creando…' : 'Crear organización'}
        </button>
      </form>
    </section>
  );
}

export function DetalleTenant(): JSX.Element {
  const { id = '' } = useParams();
  const { puede } = useSesionOperador();
  const cliente = useQueryClient();

  const { data: tenant, isLoading } = useQuery({
    queryKey: ['plataforma', 'tenant', id],
    queryFn: () => api.get<TenantDetalle>(`/plataforma/tenants/${id}`),
  });

  const { data: planes } = useQuery({
    queryKey: ['plataforma', 'planes'],
    queryFn: () => api.get<PlanInfo[]>('/plataforma/planes'),
  });

  const cambiar = useMutation({
    mutationFn: (datos: unknown) => api.put<TenantDetalle>(`/plataforma/tenants/${id}/suscripcion`, datos),
    onSuccess: async () => cliente.invalidateQueries({ queryKey: ['plataforma'] }),
  });

  const reactivar = useMutation({
    mutationFn: (datos: unknown) => api.post<TenantDetalle>(`/plataforma/tenants/${id}/reactivar`, datos),
    onSuccess: async () => cliente.invalidateQueries({ queryKey: ['plataforma'] }),
  });

  const [planCodigo, setPlanCodigo] = useState<string | null>(null);
  const [asientos, setAsientos] = useState<number | null>(null);
  const [estado, setEstado] = useState<EstadoSuscripcion | null>(null);
  const [motivo, setMotivo] = useState('');

  if (isLoading) return <Cargando />;
  if (!tenant) return <Vacio titulo="Organización no encontrada" />;

  const sinSuscripcion = tenant.estadoSuscripcion === null;
  const error = (cambiar.error ?? reactivar.error) as ErrorApi | null;

  return (
    <>
      <BotonVolver a="/plataforma/organizaciones" etiqueta="Volver a Organizaciones" />

      <div className="cabecera-pagina">
        <div>
          <h1>{tenant.razonSocial}</h1>
          <p>
            {tenant.slug}
            {tenant.ruc ? ` · RUC ${tenant.ruc}` : ''} · alta {formatearFecha(tenant.creadoEl)}
          </p>
        </div>
        {tenant.estadoSuscripcion ? (
          <Etiqueta tono={TONO_ESTADO[tenant.estadoSuscripcion] ?? 'gris'}>{tenant.estadoSuscripcion}</Etiqueta>
        ) : (
          <Etiqueta tono="roja">Sin suscripción</Etiqueta>
        )}
      </div>

      {(cambiar.isError || reactivar.isError) && error ? <Aviso tono="error">{error.message}</Aviso> : null}

      {!tenant.activo ? (
        <Aviso tono="atencion">
          La organización está inactiva: sus usuarios no pueden iniciar sesión. Reactívela creando una
          suscripción nueva.
        </Aviso>
      ) : null}

      <div className="rejilla-indicadores">
        <Indicador titulo="PLAN" valor={tenant.plan ?? '—'} nota={tenant.planCodigo ?? undefined} />
        <Indicador
          titulo="ASIENTOS"
          valor={`${tenant.asientosConsumidos} / ${tenant.asientosContratados ?? '—'}`}
          nota="Facturables en uso sobre contratados"
        />
        <Indicador
          titulo="PRECIO POR ASIENTO"
          valor={tenant.precioAsientoMensualPen ? formatearMoneda(tenant.precioAsientoMensualPen) : '—'}
          nota="mensual"
        />
        <Indicador
          titulo="TOTAL CON IGV"
          valor={tenant.totalMensualPen ? formatearMoneda(tenant.totalMensualPen) : '—'}
          nota={tenant.periodoInicio ? `Desde ${formatearFecha(tenant.periodoInicio)}` : undefined}
        />
      </div>

      {puede('suscripcion:administrar') ? (
        <section className="panel" style={{ marginBottom: 16 }}>
          <div className="panel-cabecera">
            <h2>{sinSuscripcion ? 'Reactivar' : 'Cambiar suscripción'}</h2>
          </div>
          <form
            className="panel-cuerpo"
            onSubmit={(e) => {
              e.preventDefault();
              if (sinSuscripcion) {
                reactivar.mutate({
                  planCodigo: planCodigo ?? planes?.[0]?.codigo,
                  asientosContratados: asientos ?? 1,
                });
              } else {
                cambiar.mutate({
                  ...(planCodigo ? { planCodigo } : {}),
                  ...(asientos !== null ? { asientosContratados: asientos } : {}),
                  ...(estado ? { estado } : {}),
                  ...(motivo ? { motivo } : {}),
                });
              }
            }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
              <div className="campo">
                <label htmlFor="plan-cambio">Plan</label>
                <select
                  id="plan-cambio"
                  value={planCodigo ?? tenant.planCodigo ?? ''}
                  onChange={(e) => setPlanCodigo(e.target.value)}
                >
                  {(planes ?? []).map((p) => (
                    <option key={p.codigo} value={p.codigo}>
                      {p.nombre} · {formatearMoneda(p.precioAsientoMensualPen)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="campo">
                <label htmlFor="asientos-cambio">Asientos contratados</label>
                <input
                  id="asientos-cambio"
                  type="number"
                  min={Math.max(1, tenant.asientosConsumidos)}
                  max={10000}
                  value={asientos ?? tenant.asientosContratados ?? 1}
                  onChange={(e) => setAsientos(Number(e.target.value))}
                />
              </div>
              {!sinSuscripcion ? (
                <div className="campo">
                  <label htmlFor="estado-cambio">Estado</label>
                  <select
                    id="estado-cambio"
                    value={estado ?? ''}
                    onChange={(e) => setEstado((e.target.value || null) as EstadoSuscripcion | null)}
                  >
                    <option value="">Sin cambio</option>
                    {ESTADOS_SUSCRIPCION.map((e) => (
                      <option key={e} value={e}>
                        {ETIQUETA_ESTADO_SUSCRIPCION[e]}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              {!sinSuscripcion ? (
                <div className="campo">
                  <label htmlFor="motivo">Motivo (queda en la bitácora)</label>
                  <input id="motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={500} />
                </div>
              ) : null}
            </div>

            {estado === 'CANCELADA' ? (
              <Aviso tono="atencion">
                Cancelar desactiva la organización: sus usuarios dejan de poder iniciar sesión de
                inmediato. El histórico de facturación se conserva.
              </Aviso>
            ) : null}

            <button
              type="submit"
              className="boton"
              disabled={cambiar.isPending || reactivar.isPending}
              style={{ marginTop: 10 }}
            >
              {sinSuscripcion ? 'Reactivar organización' : 'Aplicar cambios'}
            </button>
          </form>
        </section>
      ) : null}

      <section className="panel" style={{ marginBottom: 16 }}>
        <div className="panel-cabecera">
          <h2>Cierres facturados</h2>
          <span className="nav-contador">{tenant.cierres.length}</span>
        </div>
        {tenant.cierres.length > 0 ? (
          <div className="tabla-contenedor">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Período</th>
                  <th>Pico de uso</th>
                  <th>Contratados</th>
                  <th>Precio</th>
                  <th>Importe</th>
                </tr>
              </thead>
              <tbody>
                {tenant.cierres.map((c) => (
                  <tr key={c.periodo}>
                    <td style={{ fontWeight: 600 }}>{c.periodo.slice(0, 7)}</td>
                    <td className="numero-registro">{c.asientosPico}</td>
                    <td className="numero-registro">{c.asientosContratados}</td>
                    <td>{formatearMoneda(c.precioAsientoMensualPen)}</td>
                    <td>{formatearMoneda(c.importePen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Vacio titulo="Sin cierres" detalle="Aún no se ha cerrado ningún período para esta organización." />
        )}
      </section>

      <section className="panel">
        <div className="panel-cabecera">
          <h2>Histórico de licencia</h2>
          <span className="nav-contador">{tenant.eventos.length}</span>
        </div>
        {tenant.eventos.length > 0 ? (
          <div className="tabla-contenedor">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Evento</th>
                  <th>Uso</th>
                  <th>Detalle</th>
                </tr>
              </thead>
              <tbody>
                {tenant.eventos.map((e, i) => (
                  <tr key={`${e.fechaHora}-${i}`}>
                    <td>{formatearFechaHora(e.fechaHora)}</td>
                    <td>
                      <Etiqueta tono={e.tipo === 'INTENTO_BLOQUEADO' ? 'roja' : 'gris'}>{e.tipo}</Etiqueta>
                    </td>
                    <td className="numero-registro">
                      {e.asientosConsumidos ?? '—'} / {e.asientosContratados ?? '—'}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--texto-medio)' }}>
                      {Object.entries(e.detalle)
                        .map(([k, v]) => `${k}: ${String(v)}`)
                        .join(' · ') || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Vacio titulo="Sin eventos" />
        )}
      </section>
    </>
  );
}
